import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

type AppType = 'creator' | 'fan' | null;

class KorPlatformClass {
  private _app: AppType = null;

  constructor() {
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      const port = window.location.port;

      // Creator app
      if (
        hostname === 'app.korlink.io' ||
        hostname === 'admin.korcrew.com' ||
        hostname === 'staging.admin.korcrew.com' ||
        (hostname === 'localhost' && port === '3002')
      ) {
        this._app = 'creator';
      }
      // Fan app
      else if (
        hostname === 'app.korcrew.com' ||
        hostname === 'korcrew.com' ||
        hostname === 'staging.korcrew.com' ||
        hostname === 'beta.korcrew.com' ||
        (hostname === 'localhost' && port === '3001')
      ) {
        this._app = 'fan';
      }
    }
  }

  get is(): AppType {
    return this._app;
  }

  get isCreator(): boolean {
    return this._app === 'creator';
  }

  get isFan(): boolean {
    return this._app === 'fan';
  }
}

export const KorPlatform = new KorPlatformClass();

export function formatBadgeNumber(num: number, total: number) {
  if (total === 0) {
    return num.toString();
  }

  if ((!num && num !== 0) || !total) {
    return;
  }

  const length = total.toString().length;

  const digits = num.toString();
  const paddingLength = length - digits.length;

  if (paddingLength > 0) {
    const padding = '0'.repeat(paddingLength);
    return `${padding}${digits}`;
  } else if (paddingLength === 0) {
    return digits;
  } else {
    if (digits.length < 3) {
      return digits;
    }

    const reversedDigits = digits.split('').reverse();

    const formattedDigits = reversedDigits.reduce((acc, digit, index) => {
      return index > 0 && index % 3 === 0 ? `${digit},${acc}` : `${digit}${acc}`;
    }, '');

    return formattedDigits;
  }
}

export const getBadgeInfo = (info: string | undefined) => {
  if (!info) return null;
  try {
    const parsed = JSON.parse(info);
    if (typeof parsed === 'object' && parsed !== null && 'color' in parsed) {
      return parsed;
    }
    if (typeof parsed === 'object' && parsed !== null && 'badgeInfo' in parsed) {
      return JSON.parse(parsed.badgeInfo);
    }
  } catch (error) {}
  return null;
};

export const detectInAppBrowser = (): boolean => {
  const userAgent = navigator?.userAgent;
  const inAppBrowser =
    /(wv|webView|ig|Instagram|FB_IAB|FB4A|FBAN|Facebook|Twitter|TikTok|LinkedIn|Reddit|Snapchat|WhatsApp|WeChat|Line|Pinterest|Viber)/gi;
  return inAppBrowser.test(userAgent);
};

export const abbreviateNumber = (num: number): string => {
  if (!num) {
    return '';
  }
  if (num >= 1e9) {
    return (num / 1e9).toFixed(1) + 'B';
  }
  if (num >= 1e6) {
    return (num / 1e6).toFixed(1) + 'M';
  }
  if (num >= 1e3) {
    return (num / 1e3).toFixed(1) + 'K';
  }
  return num?.toString();
};

export const sanitizeFilename = (filename: string) => {
  // Replace spaces with underscores
  let sanitizedFilename = filename.replace(/\s+/g, '_');

  // Remove all characters except alphanumeric, underscores, dots, and hyphens
  sanitizedFilename = sanitizedFilename.replace(/[^a-zA-Z0-9._-]/g, '');

  return sanitizedFilename;
};

export function cleanMessagePreview(preview?: unknown): string {
  if (!preview) return '';

  return (
    String(preview)
      .replace(/&nbsp;/gi, ' ')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/gi, "'")
      .replace(/&amp;/gi, '&')
      // Turn block-level tags into spaces so words don't run together.
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<\/p>/gi, ' ')
      .replace(/<\/div>/gi, ' ')
      // Strip every remaining tag so previews never show formatting markup.
      .replace(/<\/?[a-zA-Z][^>]*>/g, '')
      .replace(/\s+/g, ' ')
      .trim()
  );
}

type PostTitleInput = {
  name?: string | null;
  parent_post_name?: string | null;
  rich_description?: string | null;
  description?: string | null;
};

const POST_TITLE_MAX_PREVIEW_LENGTH = 40;

/**
 * Label for a post in breadcrumbs and headers.
 *
 * Posts may have no title, so `name` alone rendered "null" in the crumb. Fall
 * back to a stripped, truncated preview of the post body, then to "Post".
 */
export function getPostTitle(post?: PostTitleInput | null): string {
  const title = [post?.name, post?.parent_post_name].find(
    (value) => typeof value === 'string' && value.trim().length > 0,
  );
  if (title) return title.trim();

  const preview = cleanMessagePreview(post?.rich_description || post?.description);
  if (preview) {
    return preview.length > POST_TITLE_MAX_PREVIEW_LENGTH
      ? `${preview.slice(0, POST_TITLE_MAX_PREVIEW_LENGTH).trimEnd()}…`
      : preview;
  }

  return 'Post';
}

/**
 * @deprecated Prefer {@link cleanMessagePreview}. Kept as a thin alias so existing
 * call sites stay on the same hardened, cross-platform preview-strip logic.
 */
export function sanitizePreviewText(raw?: string): string {
  return cleanMessagePreview(raw);
}
